import ApartmentIcon from "@mui/icons-material/Apartment";
import BlockFlippedIcon from "@mui/icons-material/BlockFlipped";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import ResponsiveButton from "components/ResponsiveButton";
import { useSetBreadcrumbs } from "code/setBreadcrumbsHelper";
import { LISTING_PAGE_LEVEL } from "constants/BreadcrumbLevel";

interface NoCompanyAccessPageProps {
	/**
	 * When true, renders the CV2 "access not enabled" variant: a bare "you do not have access"
	 * message with no call-to-action. Used by `RequireCv2Access` for users whose `/api` is gated,
	 * so the request-access CTA (and the accounts route it links to) is not available to them.
	 */
	accessNotEnabled?: boolean;
}

export default function NoCompanyAccessPage({
	accessNotEnabled = false
}: NoCompanyAccessPageProps) {
	const navigate = useNavigate();
	useSetBreadcrumbs(accessNotEnabled ? "No Access" : "No Company Access", LISTING_PAGE_LEVEL);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				textAlign: "center",
				flexGrow: 1,
				my: { xs: 4, md: 10 },
				px: 2
			}}
		>
			<Box sx={{ position: "relative", color: "secondary.light50", lineHeight: 0, mb: 4 }}>
				<ApartmentIcon sx={{ fontSize: 175 }} />
				<BlockFlippedIcon
					sx={{
						fontSize: 92,
						position: "absolute",
						top: -4,
						right: -20,
						bgcolor: "background.default",
						borderRadius: "50%"
					}}
				/>
			</Box>

			<Typography variant="display4" component="h1" sx={{ mb: 1.5 }}>
				{accessNotEnabled ? "You do not have access" : "You do not have access to any companies"}
			</Typography>

			{!accessNotEnabled && (
				<>
					<Typography variant="text16" sx={{ maxWidth: 545, mb: 1.5 }}>
						Please request access to an account or request a new company account via &ldquo;My
						accounts&rdquo; or speak with your account administrator.
					</Typography>

					<ResponsiveButton variant="contained" onClick={() => navigate("/MyProfile?tab=Accounts+%26+Roles")}>
						Go to My Accounts
					</ResponsiveButton>
				</>
			)}
		</Box>
	);
}
